import React, { Suspense, lazy } from 'react'
import { css } from '@emotion/core'
import type { Gender, Person } from '@fs/zion-tree-types'
import { usePortraitSelector } from '@fs/zion-portrait-selector'
import { MediaCamera } from '@fs/zion-icon'
import { useTranslation } from 'react-i18next'
import { WhiteTheme, Avatar } from '@fs/zion-ui'
import { type UsePortraitSelectorReturnType } from '../banners/PortraitOverlay'

const PortraitOverlay = lazy(() => import('../banners/PortraitOverlay'))

const portraitSelectorCss = css`
  position: absolute;
  top: 0;
  inset-inline-start: 0;
  height: 24px;
  width: 24px;
  border-radius: 24px;
  background: none;
  cursor: pointer;
  transition: all 0.15s;
  border: none;
  outline: none;
  backdrop-filter: brightness(0.6);
  display: flex;
  justify-content: center;
  align-items: center;
  opacity: 0;
  font-size: 12px;
  text-align: center;
  text-shadow: 0 0 8px rgb(0, 0, 0, 0.5);
  &[data-status],
  &:hover {
    opacity: 1;
  }

  &[avatar-size='sm'] {
    height: 40px;
    width: 40px;
    border-radius: 40px;
  }
  &[avatar-size='md'] {
    height: 64px;
    width: 64px;
    border-radius: 64px;
  }
  &[avatar-size='lg'] {
    height: 88px;
    width: 88px;
    border-radius: 88px;
  }
`
const fontSizeCss = css`
  font-size: inherit;
  &[avatar-size='xs'] {
    font-size: 4px;
  }
  &[avatar-size='sm'] {
    font-size: 6px;
  }
  &[avatar-size='md'] {
    font-size: 10px;
  }
`

type Props = {
  focusPerson?: Person
  size: 'xs' | 'sm' | 'md' | 'lg'
}

/**
 * Person does not provide a way for a callback to ran when the avatar is clicked.
 * but gold is going to add a way to hide the avatar. At that point, we'll refactor
 * the person banner to use Avatar directly so we can wrap it with this instead
 * of absolute positioning this component.
 *
 */
export default function PortraitSelectorButton({ focusPerson, size }: Props): JSX.Element | null {
  const [t] = useTranslation()
  const portraitOverlayProps: UsePortraitSelectorReturnType = usePortraitSelector()
  const nonReadyStatusKey =
    focusPerson?.portraitStatus === 'failed'
      ? 'tree-person.upload-failed.description'
      : 'person-page-banner.processing.badge'
  const status =
    focusPerson?.portraitStatus && focusPerson?.portraitStatus !== 'ready' ? t(nonReadyStatusKey) : undefined

  return focusPerson ? (
    <div style={{ position: 'relative' }}>
      <Avatar
        src={focusPerson?.portraitUrl}
        sex={(focusPerson?.gender?.toLowerCase() as Lowercase<Gender> | undefined) ?? 'unknown'}
        size={size}
        gray={focusPerson.deleted}
      />
      {!focusPerson?.readOnly && !focusPerson.deleted && (
        <>
          <button
            css={portraitSelectorCss}
            avatar-size={size}
            type="button"
            data-testid="update-portrait-button"
            onClick={focusPerson && !focusPerson.readOnly ? portraitOverlayProps.handleClick : undefined}
            aria-label={t('tree-person.update-portrait.aria-label', {
              name: focusPerson?.name ?? t('tree-person.unknown.label'),
            })}
            data-status={status}
          >
            <WhiteTheme avatar-size={size} css={fontSizeCss}>
              {status ?? <MediaCamera color="white" size={size} />}
            </WhiteTheme>
          </button>
          <Suspense fallback="">
            {(portraitOverlayProps.isOpen || portraitOverlayProps.transitioning) && (
              <PortraitOverlay person={focusPerson} overlayProps={portraitOverlayProps} />
            )}
          </Suspense>
        </>
      )}
    </div>
  ) : null
}
